import { useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { ufoEventOnboardingInviteUsers } from '@/components/signup/utils/ufo-events';
import { UfoCustomTimingKey } from '@/components/signup/utils/ufo.types';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';

import { useCloudStatus } from '../../../../../hooks/use-cloud-status';
import { ANALYTICS_PREFIX } from '../../../common/constants';

import { findRoleIdWithName, ProjectRoleIds } from './use-invite-step-fetch-project-role-ids';
import { parseJiraEditionFromLicenseInformation } from './utils';

import type {
	InviteTarget,
	UseResolveInviteTarget,
} from './use-jira-onboarding-invites-handler.types';

/**
 * Resolves the data required to send onboarding-wizard invites for a created project:
 * project id (via task polling), project role ids, and the Jira edition.
 *
 * Step A polls the project-create task (serial, required first). Step B fetches roles and
 * licence information in parallel. Any failure emits its failure analytics
 * and UFO substep end, then resolves to `null` so the caller can silently no-op — no
 * user-facing error is surfaced.
 *
 * Do NOT use outside of fg: spice-refactor-jira-onboarding-wizard-invites, contact #help-spice for details
 */
export const useResolveInviteTarget = (): UseResolveInviteTarget => {
	const stargateClient = useBxpWebStargateClient();
	const { cloudId } = useCloudStatus();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const hasResolvedRef = useRef(false);

	const resolveProjectInformation = async (taskId: string): Promise<InviteTarget | null> => {
		if (hasResolvedRef.current) {
			return null;
		}
		hasResolvedRef.current = true;

		// Step A: poll the project-create task (serial, required first)
		let projectId: string;
		ufoEventOnboardingInviteUsers.startSubstep(
			UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_POLLING_PROJECT_CREATE_TASK,
		);
		try {
			const { result } = await stargateClient.pollTaskUntilComplete(taskId, cloudId);
			projectId = result.projectId;
		} catch (error) {
			createAnalyticsEvent({
				action: 'failed',
				actionSubject: 'polling',
				actionSubjectId: `${ANALYTICS_PREFIX}InviteCreateProjectTaskPolling`,
				eventType: 'track',
				attributes: {
					taskId,
					error: error instanceof Error ? error.message : String(error),
				},
			}).fire();
			return null;
		} finally {
			ufoEventOnboardingInviteUsers.endSubstep(
				UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_POLLING_PROJECT_CREATE_TASK,
			);
		}

		if (!projectId) {
			return null;
		}

		// Step B: fetch roles + licence information in parallel
		ufoEventOnboardingInviteUsers.startSubstep(
			UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_FETCHING_ROLES,
		);
		ufoEventOnboardingInviteUsers.startSubstep(
			UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_CHECKING_LICENSE,
		);

		const rolesPromise = stargateClient.fetchProjectRoles(cloudId, projectId).then(
			(roles): ProjectRoleIds => ({
				viewer: findRoleIdWithName(['viewer'], roles),
				member: findRoleIdWithName(['member', 'user'], roles),
				admin: findRoleIdWithName(['administrator'], roles),
			}),
			(error): null => {
				createAnalyticsEvent({
					eventType: 'track',
					action: 'failed_to_fetch_project_roles',
					actionSubject: 'onboardingWizardInviteStep',
					attributes: {
						cloudId,
						projectId,
						error: error instanceof Error ? error.message : String(error),
					},
				}).fire();
				return null;
			},
		);

		const editionPromise = stargateClient.getLicenceInformationStatus({ cloudId }).then(
			(licenseInformation): string => parseJiraEditionFromLicenseInformation(licenseInformation),
			(error): null => {
				createAnalyticsEvent({
					action: 'failed',
					actionSubject: 'getLicenceInformationStatus',
					eventType: 'track',
					attributes: {
						cloudId,
						projectId,
						error: error instanceof Error ? error.message : String(error),
					},
				}).fire();
				return null;
			},
		);

		const [projectRoleIds, jiraEdition] = await Promise.all([rolesPromise, editionPromise]);

		ufoEventOnboardingInviteUsers.endSubstep(
			UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_FETCHING_ROLES,
		);
		ufoEventOnboardingInviteUsers.endSubstep(
			UfoCustomTimingKey.JIRA_ONBOARDING_WIZARD_INVITE_CHECKING_LICENSE,
		);

		if (projectRoleIds === null) {
			return null;
		}

		return { projectId, projectRoleIds, jiraEdition };
	};

	return { resolveProjectInformation };
};
